import { AfterViewInit, Component, input, model, ViewEncapsulation, inject } from '@angular/core';
import { NgControl } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';

import { ApContentService } from '@ap/content';
import { AbstractInputComponent, ApOptions, InputType, InputmodeType } from '@ap/input';
import { ApInputConstraintService } from '@ap/input/constraint';
import { ApPincodeValidator } from '@ap/input/validators';
import { ApFormErrorsComponent } from '@ap/form/errors';

import content from './pincode-input.content.json';

@Component({
    selector: 'ap-pincode-input',
    templateUrl: './pincode-input.component.html',
    styleUrls: ['./pincode-input.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [ApFormErrorsComponent, NgTemplateOutlet],
    providers: [ApInputConstraintService]
})
export class ApPincodeInputComponent extends AbstractInputComponent implements AfterViewInit {
    protected control = inject(NgControl, { self: true, optional: true });
    protected readonly inputConstraintService = inject(ApInputConstraintService);
    protected readonly contentService = inject(ApContentService);

    /**
     * Maxlength of a pincode is 5
     */
    protected readonly maxlength = input<number>(5);

    /**
     * Characters that are stripped
     */
    protected readonly stripPattern = input<RegExp>(/[^0-9]/g);

    /**
     * Pincode does not have a helptext
     * @ignore
     */
    public readonly helpText = model<string>(null);

    /**
     * Pincode type is password
     */
    protected readonly type = input<InputType>('password');

    /**
     * Pincode create array of boxes
     */
    protected boxes = new Array(this.maxlength());

    /**
     * Inputmode (Standard HTML inputmode types)
     */
    protected readonly inputmode = input<InputmodeType>('numeric');

    override readonly options = input<ApOptions>(null);

    constructor() {
        super();
        this.setDefaultContent(content, 'pincode-input');
    }

    /**
     * Is the current box the active box?
     */
    protected isActive(index: number): boolean {
        if (this.value?.length === this.maxlength()) {
            return index === this.maxlength() - 1;
        }
        return this.value ? this.value.length === index : index === 0;
    }

    /**
     * Check if box should be filled
     */
    protected isFilled(index: number): boolean {
        if (!this.value) {
            return false;
        }

        return this.value.length > index;
    }

    /**
     * After init bind the control validation
     */
    public ngAfterViewInit(): void {
        const control = this.control?.control;
        if (control) {
            control.addValidators([ApPincodeValidator.validate]);
            control.updateValueAndValidity();
        }
    }

    /**
     * Empty value if not completely filled in
     * Function is called from onBlur function of AbstractInputComponent
     */
    protected formatValue(value: string): string {
        if (value && value.length < 5) {
            this.input().nativeElement.value = '';
            return '';
        }
        return value;
    }
}
